import React, { useEffect } from "react";
import { Box, Button, Chip, List, ListItem, ListItemText, Tooltip, Typography } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CloudQueueOutlinedIcon from "@mui/icons-material/CloudQueueOutlined";
import FormatQuoteRoundedIcon from "@mui/icons-material/FormatQuoteRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import UpdateRoundedIcon from "@mui/icons-material/UpdateRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import apis from "../../api";
import VisitorMap from "../../component/VisitorMap";
import { setUserName } from "../../store/reducers/user";
import "./HomePage.css";

const reveal = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0 },
};

const features = [
  { icon: <AutoAwesomeRoundedIcon />, content: <><strong>Efficient no-coding</strong> PRS model training experience</> },
  { icon: <CloudQueueOutlinedIcon />, content: <>Currently enables online training for <strong>7 single- and multi-ancestry</strong> PRS methods</> },
  { icon: <ScienceOutlinedIcon />, content: <>An offline pipeline supporting <strong>10</strong> methods for PRS training without requiring individual-level data</> },
  { icon: <HubOutlinedIcon />, content: <>Over <strong>27,000</strong> queryable datasets from the GWAS Catalog and <strong>2,400</strong> from the FinnGen</> },
  { icon: <VerifiedRoundedIcon />, content: <>Publicly available PRS weights for over <strong>8,000</strong> phenotypes across <strong>5</strong> superpopulations and <strong>7</strong> studies</> },
];

const updates = [
  { date: "2026-04-14:", content: "PennPRS v1.1.0 is now available. This update introduces a redesigned QC pipeline and an improved user job center for enhanced usability, along with UI improvements and various bug fixes." },
  { date: "2025-11-22:", content: "PennPRS v1.0.3 is now available. This update adds example input files for both continuous and binary traits, supports logOR in the input format, and includes an improved tutorial." },
  { date: "2025-04-6:", content: "PennPRS version 1.0.2 is now available. This update features an optimized login/signup system and supports direct queries from the FinnGen project for multi-ancestry analysis." },
  { date: "2025-03-12:", content: "PennPRS version 1.0.1 is now available. This update includes supporting direct query from over 2,400 binary disease phenotypes from the FinnGen project for single-ancestry analysis." },
  { date: "2025-02-27:", content: "The user interface has been updated based on feedback. Additionally, the log file and results have been restructured for improved clarity and organization." },
  { date: "2025-01-25:", content: "PennPRS version 1.0.0 was released." },
  { date: "2024-11-19:", content: <><a href="https://pennprs.gitbook.io/pennprs" target="_blank" rel="noreferrer">tutorial</a> is available for users.</> },
  { date: "2024-10-13:", content: "PennPRS is now live!" },
  { date: "2024-09-25:", content: "Internal testing phase completed." },
  { date: "2024-05-15:", content: "Demo version of PennPRS is available." },
];

const Reveal = ({ children, className = "", delay = 0 }) => (
  <motion.div
    className={className}
    variants={reveal}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.18 }}
    transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

const ActionCard = ({ eyebrow, title, description, icon, onClick, accent }) => (
  <motion.button
    type="button"
    className="home-action-card"
    style={{ "--card-accent": accent }}
    onClick={onClick}
    whileHover={{ y: -7 }}
    whileTap={{ scale: 0.985 }}
  >
    <span className="home-action-icon">{icon}</span>
    <span className="home-action-copy">
      <span className="home-action-eyebrow">{eyebrow}</span>
      <span className="home-action-title">{title}</span>
      <span className="home-action-description">{description}</span>
    </span>
    <ArrowForwardRoundedIcon className="home-action-arrow" />
  </motion.button>
);

export const HomePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userEmail = useSelector((state) => state.user.email);

  useEffect(() => {
    const data = { email: userEmail, username: "", lastname: "", institution: "", city: "", state: "", country: "" };
    apis.get_user_info(data)
      .then((response) => dispatch(setUserName(response.data.username)))
      .catch((error) => console.error("Error:", error.message));
  }, [dispatch, userEmail]);

  const handleRedirectSA = () => navigate(userEmail === null ? "/login" : "/sa");
  const handleRedirectMA = () => navigate(userEmail === null ? "/login" : "/ma");
  const handleRedirectTutorial = () => window.open("https://pennprs.gitbook.io/pennprs", "_blank", "noopener,noreferrer");
  const scrollToFeatures = () => document.getElementById("home-features")?.scrollIntoView({ behavior: "smooth" });

  return (
    <main className="home-page">
      <section className="home-hero">
        <div className="home-orb home-orb-one" />
        <div className="home-orb home-orb-two" />
        <div className="home-grid" />
        <div className="home-spotlight" aria-hidden="true">
          <span className="home-spotlight-ring home-spotlight-ring-one" />
          <span className="home-spotlight-ring home-spotlight-ring-two" />
          <span className="home-spotlight-ring home-spotlight-ring-three" />
        </div>

        <div className="home-shell home-hero-layout">
          <motion.div className="home-hero-copy" initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}>
            <motion.div className="home-spotlight-logo" initial={{ opacity: 0, scale: 0.75 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.65, delay: 0.08 }}>
              <img src="/Logo_PNG/FullColor.png" alt="PennPRS" />
            </motion.div>
            <Chip icon={<AutoAwesomeRoundedIcon />} label="Precision medicine · Cloud computing" className="home-kicker" />
            <Typography component="h1" className="home-title">
              <span>PennPRS</span>
            </Typography>
            <Typography component="p" className="home-intro">
              PennPRS is a centralized cloud computing platform for efficient polygenic risk score (PRS) model training in precision medicine.
              Users can either upload their own GWAS summary data or directly query data from public <a href="/data" target="_blank" rel="noreferrer">data sources</a> we provide.
              PennPRS supports both <a href="https://pennprs.gitbook.io/pennprs/user-manual/single-ancestry-analysis" target="_blank" rel="noreferrer">single-ancestry</a> and <a href="https://pennprs.gitbook.io/pennprs/user-manual/multi-ancestry-analysis" target="_blank" rel="noreferrer">multi-ancestry</a> PRS training.
            </Typography>
            <Box className="home-hero-actions">
              <Button variant="contained" size="large" endIcon={<ArrowForwardRoundedIcon />} onClick={handleRedirectSA} className="home-primary-button">Start an analysis</Button>
              <Button variant="outlined" size="large" onClick={() => navigate("/data")} className="home-secondary-button">Explore queryable data</Button>
            </Box>
          </motion.div>
        </div>
        <motion.button
          type="button"
          className="home-scroll-cue"
          onClick={scrollToFeatures}
          aria-label="Scroll down to explore PennPRS features"
          animate={{ y: [0, 7, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <span>Explore PennPRS</span>
          <KeyboardArrowDownRoundedIcon />
        </motion.button>
      </section>

      <section id="home-features" className="home-section home-feature-section">
        <div className="home-shell">
          <Reveal className="home-section-heading">
            <span className="home-overline">Built for modern PRS research</span>
            <Typography component="h2">One platform, from GWAS data to trained models</Typography>
          </Reveal>
          <div className="home-feature-grid">
            {features.map((feature, index) => (
              <Reveal className={`home-feature-card ${index === 4 ? "home-feature-wide" : ""}`} delay={index * 0.06} key={index}>
                <span className="home-feature-icon">{feature.icon}</span>
                <Typography component="p">{feature.content}</Typography>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="home-section home-insight-section">
        <div className="home-shell home-insight-grid">
          <Reveal className="home-panel home-updates-panel">
            <div className="home-panel-header">
              <span className="home-panel-icon"><UpdateRoundedIcon /></span>
              <div><span className="home-overline">What’s new</span><Typography component="h2">Updates</Typography></div>
            </div>
            <Typography component="p" className="home-panel-lead">For more information, please visit our <a href="/updates" target="_blank" rel="noreferrer"><strong>updates page</strong></a>.</Typography>
            <div className="home-update-list">
              <List dense disablePadding>
                {updates.map((update) => (
                  <ListItem key={update.date} disableGutters className="home-update-item">
                    <span className="home-update-dot" />
                    <ListItemText primary={<Typography component="p"><strong>{update.date}</strong> {update.content}</Typography>} />
                  </ListItem>
                ))}
              </List>
            </div>
          </Reveal>

          <Reveal className="home-panel home-citation-panel" delay={0.1}>
            <div className="home-panel-header">
              <span className="home-panel-icon"><FormatQuoteRoundedIcon /></span>
              <div><span className="home-overline">Research</span><Typography component="h2">Citation</Typography></div>
            </div>
            <Typography component="p" className="home-panel-lead">If you find PennPRS useful, please cite us in your work:</Typography>
            <Tooltip title="Navigate to Paper" arrow>
              <motion.button type="button" className="home-citation-card" onClick={() => window.open("https://doi.org/10.1101/2025.02.07.25321875", "_blank", "noopener,noreferrer")} whileHover={{ y: -4 }}>
                <span className="home-citation-mark">“</span>
                <span>Jin, J., Li, B., Wang, X., Yang, X., Li, Y., Wang, R., Ye, C., Shu, J., Fan, Z., Xue, F., Ge, T., Ritchie, M.D., Pasaniuc, B., Wojcik, G., Zhao, B. PennPRS: a Centralized Cloud Computing Platform for Efficient Polygenic Risk Score Training in Precision Medicine. medRxiv, 2025. DOI: 10.1101/2025.02.07.25321875.</span>
                <ArrowForwardRoundedIcon />
              </motion.button>
            </Tooltip>
            <div className="home-citation-stats">
              <div><strong>7</strong><span>online methods</span></div>
              <div><strong>5</strong><span>superpopulations</span></div>
              <div><strong>8K+</strong><span>phenotypes</span></div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="home-section home-start-section">
        <div className="home-shell">
          <Reveal className="home-section-heading home-section-heading-light">
            <span className="home-overline">Choose your next step</span>
            <Typography component="h2">Select one of the following options to start.</Typography>
            <Typography component="p">Train a model, explore the documentation, or get answers from the PennPRS team.</Typography>
          </Reveal>
          <div className="home-action-grid">
            <ActionCard eyebrow="Train" title="📊 Single-Ancestry Analysis" description="PRS model training using GWAS data from a single ancestry." icon={<ScienceOutlinedIcon />} onClick={handleRedirectSA} accent="#54d6c4" />
            <ActionCard eyebrow="Train" title="📊 Multi-Ancestry Analysis" description="PRS model training using GWAS data from multiple ancestries." icon={<HubOutlinedIcon />} onClick={handleRedirectMA} accent="#76a9ff" />
            <ActionCard eyebrow="Learn" title="📃 Tutorial" description="View the tutorial to learn how to use the platform." icon={<MenuBookOutlinedIcon />} onClick={handleRedirectTutorial} accent="#f0b96a" />
            <ActionCard eyebrow="Support" title="📤 FAQs" description="View frequently asked questions." icon={<HelpOutlineRoundedIcon />} onClick={() => navigate("/faq")} accent="#d995ec" />
          </div>
        </div>
      </section>

      <footer className="home-footer">
        <div className="home-shell">
          <div className="home-footer-main">
            <div className="home-footer-brand">
              <div className="home-footer-logo"><img src="/Logo_PNG/FullColor.png" alt="PennPRS" /><span>PennPRS</span></div>
              <Typography component="p">Centralized cloud computing for efficient polygenic risk score training.</Typography>
            </div>
            <div className="home-team">
              <span className="home-footer-label">Developed by</span>
              <div className="home-team-grid">
                <a href="https://www.bingxinzhao.com/home" target="_blank" rel="noreferrer">Bingxin Zhao</a>
                <a href="https://jin93.github.io" target="_blank" rel="noreferrer">Jin Jin</a>
                <a href="https://bingxuanli.com" target="_blank" rel="noreferrer">Bingxuan Li</a>
                <a href="https://www.linkedin.com/in/xiyao-wang-04468b271/" target="_blank" rel="noreferrer">Xiyao Wang</a>
              </div>
              <Typography component="p"><em>Contact:</em> <a href="mailto:pennprs@googlegroups.com">PennPRS Team</a></Typography>
            </div>
            <div className="home-school-logos">
              <img src="/School_Logo/wharton_logo.png" alt="Wharton" />
              <img src="/School_Logo/pennmedi.png" alt="Penn Medicine" />
              <img src="/School_Logo/purdue.png" alt="Purdue" />
            </div>
          </div>
          <div className="home-visitor-wrap"><VisitorMap /></div>
          <div className="home-footer-bottom">© 2024-2026 PennPRS. All rights reserved.</div>
        </div>
      </footer>
    </main>
  );
};
